import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateTechniqueInputSchema, UpdateTechniqueInputSchema } from '@honeycomb/shared';
import { useTechnique, useCreateTechnique, useUpdateTechnique } from '@/features/techniques/api';
import { ApiClientError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';

export const Route = createFileRoute('/_authed/techniques/$id/edit')({
  component: EditTechniquePage,
});

// FormValues covers the union of both schemas (all four fields, all optional at the type level
// so the single form can be used for both create and update paths).
type FormValues = {
  term: string;
  shortDef: string;
  longDef: string;
  senseCue: string;
};

function EditTechniquePage() {
  const { id } = useParams({ from: '/_authed/techniques/$id/edit' });
  const navigate = useNavigate();
  const { data, isLoading, error: fetchError } = useTechnique(id);
  const isCurated = data != null;
  const create = useCreateTechnique();
  const update = useUpdateTechnique(id);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const formSchema = useMemo(
    () =>
      isCurated
        ? UpdateTechniqueInputSchema
        : CreateTechniqueInputSchema.omit({ id: true }),
    [isCurated],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues: { term: '', shortDef: '', longDef: '', senseCue: '' },
  });

  useEffect(() => {
    if (data?.technique) {
      reset({
        term: data.technique.term,
        shortDef: data.technique.shortDef,
        longDef: data.technique.longDef,
        senseCue: data.technique.senseCue,
      });
    }
  }, [data, reset]);

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);
    try {
      if (isCurated) {
        await update.mutateAsync(values);
      } else {
        await create.mutateAsync({ id, ...values });
      }
      await navigate({ to: '/techniques' });
    } catch (e) {
      setSubmitError(
        e instanceof ApiClientError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Save failed',
      );
    }
  };

  const is404 =
    fetchError != null &&
    (fetchError as ApiClientError).status === 404;

  if (isLoading) {
    return <div className="p-8 text-ink-soft">Loading…</div>;
  }

  if (fetchError && !is404) {
    return (
      <div className="p-8">
        <h1 className="font-serif text-2xl mb-2">Error</h1>
        <p className="text-ink-soft">
          {fetchError instanceof Error ? fetchError.message : 'Unable to load technique.'}
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: '/techniques' })}
          className="mt-4 text-sm text-ink-soft underline hover:text-ink"
        >
          Back to techniques
        </button>
      </div>
    );
  }

  return (
    <div className="p-8">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl leading-tight">
            {isCurated ? 'Edit technique' : 'Curate technique'}
          </h1>
          <p className="font-mono text-xs text-ink-faint mt-1">{id}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate({ to: '/techniques' })}
          className="text-sm text-ink-soft hover:text-ink flex-none mt-1"
        >
          Cancel
        </button>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <Input
          label="Term"
          {...register('term')}
          error={errors.term?.message}
          placeholder="e.g. Sauté"
        />
        <Input
          label="Short definition"
          {...register('shortDef')}
          error={errors.shortDef?.message}
          placeholder="One-sentence definition shown in tooltips (max 500 chars)"
        />
        <Textarea
          label="Long definition"
          {...register('longDef')}
          rows={6}
          error={errors.longDef?.message}
          placeholder="Detailed explanation for the technique detail view (max 4000 chars)"
        />
        <Input
          label="Sense cue"
          {...register('senseCue')}
          error={errors.senseCue?.message}
          placeholder="e.g. You'll hear a gentle sizzle and smell the aromatics bloom"
        />

        {submitError && (
          <div
            className="text-xs rounded-lg p-3"
            style={{
              background: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              color: 'var(--color-error-text)',
            }}
          >
            {submitError}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={() => navigate({ to: '/techniques' })}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isCurated ? 'Save' : 'Curate'}
          </Button>
        </div>
      </form>
    </div>
  );
}
